import SaveIcon from '@mui/icons-material/Save';
import { Button, ButtonGroup, Tooltip } from '@mui/material';
import { DataGridPremium, GridColDef, GridRowModel, itIT } from '@mui/x-data-grid-premium';
import { EnhancedCard } from 'components/enhanced/card/EnhancedCard';
import { IsTenantAdministrator } from 'shared/permissions/IsTenantAdministrator';
import { IsTenantCoordinator } from 'shared/permissions/IsTenantCoordinator';
import CustomizedSteppers from 'components/ui/data-display/RequestStatus';
import dayjs from 'dayjs';
import { movementService } from 'features/inventory/movements/api/movement.service';
import { IProductWarehouse } from 'features/inventory/products/types';
import { orderLineService } from 'features/inventory/purchase-orders/api/order-line.service';
import { IPurchaseOrderRow } from 'features/inventory/purchase-orders/types';
import { stockQuantityService } from 'features/inventory/stock/api/stock-quantity.service';
import { IStockQuantity } from 'features/inventory/stock/types';
import { ISupplier } from 'features/inventory/suppliers/types';
import { IWarehouse } from 'features/inventory/warehouses/types';
import { TenantPage } from 'layouts';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { contractLineService, productService } from 'shared/services';
import { useAppSelector } from 'store/store';
import { Option } from 'shared/types/general';
import toast from 'shared/utils/toast';
import { v4 as uuidv4 } from 'uuid';
import { purchaseRequestLineService } from '../api/purchase-request-line.service';
import { purchaseRequestService } from '../api/purchase-request.service';
import { IPurchaseRequest, IPurchaseRequestRow } from '../types';
import EditPurchaseRequestLineDialog from './dialog/EditPurchaseRequestLineDialog';
import PurchaseOrderPreviewDialog from './dialog/PurchaseOrderPreviewDialog';
import QuantityValidationConfirmationDialog from './dialog/QuantityValidationConfirmationDialog';
import SendPurchaseOrderEmailDialog from './dialog/SendPurchaseOrderEmailDialog';
import ExportButtonToolbar from './ExportButtonToolbar';
import PurchaseRequestAccordion from './PurchaseRequestAccordion';
import { productWarehouseService } from 'features/inventory/warehouses/api/product-warehouse.service';
import { warehouseService } from 'features/inventory/warehouses/api/warehouse.service';
import { PURCHASE_ORDER_LINE_STATUS_FILTER_IN_ARRIVO } from 'features/inventory/purchase-orders/constants';
import { PurchaseLinesColumns } from '../request-lines';
import { AutomaticPurchaseLinesColumns } from '../automatic-request-lines';

const PurchaseRequestStatus = {
  NEW: 1,
  PRE_APPROVED: 2,
  APPROVED: 3,
  REJECTED: 4,
  DELETED: 5
};

interface PurchaseRequestPageProps {
  /** Existing purchase request id. If provided, the page works in view/edit mode. */
  purchaseRequestId: string;
  /** Whether the request was automatically generated (affects columns and routing). */
  automaticGenerated: boolean;
  translationPrefix: string;
}

/**
 * Renders the Purchase Request page.
 *
 * Supports both creation and edit flows. Manages line items, product/contract/warehouse
 * selection, quantity validation, programmed (recurring) rows, status transitions and
 * order preview.
 */
const PurchaseRequestPage: React.FC<PurchaseRequestPageProps> = ({
  purchaseRequestId,
  automaticGenerated,
  translationPrefix
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (purchaseRequestId) {
    translationPrefix += '.view';
  } else {
    translationPrefix += '.new';
  }
  const toastTranlationPrefix = 'pages.purchase-requests.toast';

  // ------------------- USE STATES -------------------
  const [purchaseRequest, setPurchaseRequest] = useState<IPurchaseRequest>({
    lines: [],
    id: null,
    status: null,
    created_by: 0
  });
  const [initialRequest, setInitialRequest] = useState<IPurchaseRequest>(purchaseRequest);

  const [productToAdd, setProductToAdd] = useState<any>(null);
  const [contractToAdd, setContractToAdd] = useState<any>(null);
  const [warehouseToAdd, setWarehouseToAdd] = useState<any>(null);

  const [productOptions, setProductOptions] = useState<Option[]>([]);
  const [contractOptions, setContractOptions] = useState<Option[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<Option[]>([]);

  const [maxUnits, setMaxUnits] = useState<number | null>(null);
  const [maxDiscountedUnits, setMaxDiscountedUnits] = useState<number | null>(null);

  const [visibleProduct, setVisibleProduct] = useState<{
    code: string;
    description?: string;
    stockQuantities?: IStockQuantity[];
    productWarehouse?: IProductWarehouse[];
    orderLines?: IPurchaseOrderRow[];
    consume?: any[];
    unitPrice?: string;
    packaging?: string;
    uom?: string;
  } | null>(null);

  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [openSendEmailDialog, setOpenSendEmailDialog] = useState(false);
  const [opendConfirmationDialog, setOpendConfirmationDialog] = useState(false);

  const [confirmationValues, setConfirmationValues] = useState(null);
  const [requestPreview, setRequestPreview] = useState<Record<string, any> | null>(null);
  const [unitsPerPackage, setUnitsPerPackage] = useState<number>(0);
  const [unitPriceInfo, setUnitPriceInfo] = useState<string>(null);
  const [editContractOptions, setEditContractOptions] = useState<Option[]>([]);
  const [openEditContractDialog, setOpenEditContractDialog] = useState(false);
  const [editPurchaseRequestLine, setEditPurchaseRequestLine] = useState<any>(null);

  /* ========================= DERIVED VALUES ========================== */
  const user_warehouse = useAppSelector((state) => state.auth.warehouse);
  const [globalWarehouse, setGlobalWarehouse] = useState<string>(user_warehouse);
  const changesToSave = (): boolean => {
    return JSON.stringify(initialRequest) === JSON.stringify(purchaseRequest);
  };
  const withManufacturerLabel = (options): Option[] =>
    Array.isArray(options)
      ? options.map((option) => {
          const manufacturerId = option?.manufacturer_id ? ` - ${option.manufacturer_id}` : '';
          return {
            ...option,
            label: `${option?.code ?? ''} - ${(option?.description ?? '').replace(
              `${option?.code ?? ''} - `,
              ''
            )}${manufacturerId}`
          };
        })
      : options;
  /** Returns true when no line is still in NEW=1 or PRE_APPROVED=2 status. */
  const allLinesSelected = useMemo(() => {
    return !purchaseRequest.lines.some(
      ({ status }) =>
        status === PurchaseRequestStatus.NEW || status === PurchaseRequestStatus.PRE_APPROVED
    );
  }, [purchaseRequest.lines]);

  /** Toolbar rendered inside the DataGrid. */
  const CustomToolbar = () => <ExportButtonToolbar purchaseRequestId={purchaseRequest.id} />;

  // ----------------------------------- USE EFFECT -----------------------------------
  /**
   * Hook useEffect per l'inizializzazione e sincronizzazione dei dati dell'applicazione
   * basata sul magazzino utente (user_warehouse).
   *
   * SCENARIO CON MAGAZZINO GLOBALE DEFINITO:
   *    - Carica tutti i magazzini e filtra solo quello corrispondente a globalWarehouse
   *    - Recupera i prodotti attivi con contratto per il magazzino specificato
   *
   * SCENARIO SENZA MAGAZZINO GLOBALE:
   *    - Carica tutti i magazzini disponibili senza filtri
   */
  useEffect(() => {
    setGlobalWarehouse(user_warehouse);
    if (purchaseRequestId) {
      purchaseRequestService.get(+purchaseRequestId).then((pr) => {
        setPurchaseRequest(pr);
        setInitialRequest(pr);
      });
    }
    if (globalWarehouse) {
      {
        warehouseService
          .getAllBaseInformation()
          .then((result) => {
            return result.filter((warehouse) => warehouse.code === globalWarehouse);
          })
          .then((r) => setWarehouseOptions(r));
        productService
          .getAllBaseInformation({
            status: 'ACTIVE',
            has_contract: true,
            warehouses: globalWarehouse
          })
          .then((res) => setProductOptions(withManufacturerLabel(res)));
      }
    } else {
      warehouseService.getAllBaseInformation().then((r) => setWarehouseOptions(r));
    }
  }, [user_warehouse]);

  /**
   * Hook useEffect per caricare dinamicamente i prodotti attivi con contratto valido
   * associati al magazzino selezionato. Quando viene selezionato un magazzino (warehouseToAdd),
   * recupera automaticamente la lista dei prodotti disponibili in quel magazzino
   */
  useEffect(() => {
    if (warehouseToAdd && !globalWarehouse) {
      productService
        .getAllBaseInformation({
          status: 'ACTIVE',
          has_contract: true,
          warehouse_id: warehouseToAdd.id
        })
        .then((res) => setProductOptions(withManufacturerLabel(res)))
        .catch((error) => {
          console.error('Errore nel caricamento prodotti:', error);
          setProductOptions([]);
        });
    }
  }, [warehouseToAdd]);

  /**
   * Hook useEffect per caricare i contratti associati dopo che viene selezionato un prodotto
   * specifico in un magazzino selezionato.
   */
  useEffect(() => {
    if (productToAdd) {
      contractLineService
        .getAllBaseInformation({ product_id: productToAdd.id, warehouse_id: warehouseToAdd.id })
        .then((res) => setContractOptions(res));
    }
  }, [productToAdd]);

  /** When the selected contract changes, load summary info (max units, price). */
  useEffect(() => {
    if (contractToAdd?.id) {
      contractLineService.getSummary(contractToAdd.id).then((res) => {
        setMaxUnits(res.available_units_not_blocked);
        setMaxDiscountedUnits(res.available_discounted_units_not_blocked);
        // BOX INFO - Get the unit price (1)
        setUnitPriceInfo(res.unit_price);
      });
    } else {
      setMaxUnits(null);
      setMaxDiscountedUnits(null);
      setWarehouseToAdd(null);
      setWarehouseOptions([]);
    }
  }, [contractToAdd]);

  /**
   * Fetch aggregated product information when all selectors are filled
   */
  useEffect(() => {
    if (warehouseToAdd && productToAdd && contractToAdd) {
      const limit = 20;
      const offset = 0;

      // Fetch product warehouse information
      const fetchProductWarehouseInfo = productWarehouseService.getAllPaginated(limit, offset, {
        product_id: productToAdd.id,
        warehouse_id: warehouseToAdd.id
      });

      // Fetch stock quantities information
      const fetchStockQuantitiesInfo = stockQuantityService.getAllPaginated(limit * limit, offset, {
        product__id: productToAdd.id,
        warehouse__id: warehouseToAdd.id,
        quantity__gt: 0
      });

      // Fetch product order lines information
      const fetchProductOrderLinesInfo = orderLineService.getAllPaginated(limit, offset, {
        product_id: productToAdd.id,
        warehouse_id: warehouseToAdd.id,
        status: PURCHASE_ORDER_LINE_STATUS_FILTER_IN_ARRIVO
      });

      // Fetch product consumption information for the past year
      const paramsConsumption = {
        timestamp__gte: dayjs().subtract(1, 'year').startOf('month').format('YYYY-MM-DD'),
        timestamp__lte: dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
        product__id: productToAdd.id,
        warehouse__id: warehouseToAdd.id
      };
      const fetchProductConsumeInfo = movementService.consumptionDetail(
        limit,
        offset,
        paramsConsumption
      );
      // Execute all fetch requests concurrently and update the state with the results
      if (unitPriceInfo) {
        Promise.all([
          fetchProductWarehouseInfo,
          fetchStockQuantitiesInfo,
          fetchProductOrderLinesInfo,
          fetchProductConsumeInfo,
          productService.get(productToAdd.id)
        ]).then(
          ([
            productWarehouseRes,
            stockQuantitiesRes,
            productOrderLinesRes,
            productConsumeRes,
            productData
          ]) => {
            setVisibleProduct({
              code: productToAdd.code,
              description: productToAdd.description.replace(
                //correzione descrizion IOption
                productToAdd.code + ' - ',
                ''
              ),
              stockQuantities: stockQuantitiesRes.results,
              productWarehouse: productWarehouseRes.results,
              orderLines: productOrderLinesRes.results,
              consume: productConsumeRes.results,
              unitPrice: unitPriceInfo,
              packaging: productData?.packaging,
              uom: productData?.uom
            });
            setUnitsPerPackage(parseInt(productWarehouseRes?.results[0]?.units_per_package) || 0);
          }
        );
      }
    } else {
      // Reset visible product information if any of the dependencies change
      setVisibleProduct(null);
    }
  }, [warehouseToAdd, productToAdd, contractToAdd, unitPriceInfo]);

  /* ======================= REQUEST ACTIONS =========================== */

  /** Persist the purchase request (create or update) and navigate to its view page. */
  const saveRequest = () => {
    // Check if the purchase request is valid
    if (!purchaseRequest) {
      toast.error(t(`${toastTranlationPrefix}.invalid-request`));
      return;
    }

    // Map the lines in the purchase request
    const lines = purchaseRequest.lines.map(mapLine);

    // Create the request object
    const request = {
      ...purchaseRequest,
      lines
    };

    // Determine the service promise
    const servicePromise = purchaseRequest.id
      ? // Update the purchase request
        purchaseRequestService.update(purchaseRequest.id, request)
      : // Add the purchase request
        purchaseRequestService.add(request);

    // Call the service promise
    servicePromise
      .then((res) => {
        if (res?.id) {
          purchaseRequestService.get(res.id).then((p_res) => {
            // Update the purchase request state
            setPurchaseRequest(p_res);
            // Update the initial request state
            setInitialRequest(p_res);
            // Determine the message based on the purchase request id
            const message = p_res.id
              ? t(`${toastTranlationPrefix}.line-successfully-updated`)
              : t(`${toastTranlationPrefix}.line-successfully-added`);

            // Show the success snackbar
            enqueueSnackbar(message, { variant: 'success' });

            // Navigate to the view page of the purchase request
            navigate(`../${automaticGenerated ? 'automatic/' : ''}view/${p_res.id}`);
          });
        }
      })
      .catch((e) => {
        // Show the error toast
        toast.error(e.message);
      });
  };

  const persistRequestDraft = async (nextRequest: IPurchaseRequest) => {
    if (!nextRequest?.id) {
      return;
    }

    const payload = {
      ...nextRequest,
      lines: nextRequest.lines.map(mapLine)
    };

    const res = await purchaseRequestService.update(nextRequest.id, payload);
    if (!res?.id) {
      return;
    }

    const refreshedRequest = await purchaseRequestService.get(res.id);
    setPurchaseRequest(refreshedRequest);
    setInitialRequest(refreshedRequest);
  };

  const handleApproveSuccess = async () => {
    setOpenPreviewDialog(false);
    setOpenSendEmailDialog(false);
    enqueueSnackbar(t(`${toastTranlationPrefix}.order-sent-successfully`), {
      variant: 'success'
    });
    if (purchaseRequestId) {
      const refreshed = await purchaseRequestService.get(+purchaseRequestId);
      setPurchaseRequest(refreshed);
      setInitialRequest(refreshed);
    }
  };

  // const persistRequestDraft = async (nextRequest: IPurchaseRequest) => {
  //   if (!nextRequest?.id) {
  //     return;
  //   }

  //   const payload = {
  //     ...nextRequest,
  //     lines: nextRequest.lines.map(mapLine)
  //   };

  //   const res = await purchaseRequestService.update(nextRequest.id, payload);
  //   if (!res?.id) {
  //     return;
  //   }

  //   const refreshedRequest = await purchaseRequestService.get(res.id);
  //   setPurchaseRequest(refreshedRequest);
  //   setInitialRequest(refreshedRequest);
  // };

  /** Transition the request to PRE_APPROVED status (coordinator only). */
  const reviewRequest = () => {
    if (purchaseRequest.id) {
      purchaseRequestService
        .preApproveRequest({
          id: purchaseRequest?.id,
          lines: purchaseRequest.lines.map(mapLine)
        })
        .then(() => {
          enqueueSnackbar(t(`${toastTranlationPrefix}.pre-approved-successfully`), {
            variant: 'success'
          });
          location.reload();
        })
        .catch(() => {
          enqueueSnackbar(t(`${toastTranlationPrefix}.no-permission`), { variant: 'error' });
        });
    }
  };

  /**
   * Approve the request.
   * @param sendEmail - If true, opens the email dialog instead of direct approval.
   */
  const fulfillRequest = (sendEmail = false) => {
    if (purchaseRequestId) {
      if (purchaseRequest.lines.some((e) => e.status === 1)) {
        setOpenPreviewDialog(false);
        toast.error(t(`${toastTranlationPrefix}.pending-lines`));
        return;
      }
      if (sendEmail) {
        setOpenSendEmailDialog(true);
        setOpenPreviewDialog(false);
      } else {
        purchaseRequestService
          .approveRequest(
            {
              id: +purchaseRequestId,
              lines: purchaseRequest.lines.map(mapLine)
            },
            sendEmail
          )
          .then(() => handleApproveSuccess())
          .catch((e) => {
            toast.error(e.message);
          });
      }
    }
  };

  /** Send the approved order by email with additional supplier/contact values. */
  const sendOrder = (values) => {
    purchaseRequestService
      .approveRequest(
        {
          id: +purchaseRequestId,
          lines: purchaseRequest.lines.map(mapLine),
          values
        },
        true
      )
      .then(() => handleApproveSuccess())
      .catch((e) => {
        toast.error(e.message);
      });
  };

  // setOpenPreviewDialog(false);
  // setOpenSendEmailDialog(true);

  // const onEditCell = (params, event: MuiEvent) => {
  //   if (+params.id) {
  //     const payload = {};
  //     if (params.field == '_quantity') {
  //       payload['quantity'] = params.value;
  //     } else {
  //       payload[params.field] = params.value;
  //     }
  //     if (payload[params.field])
  //       purchaseRequestLineService.update(+params.id, { ...payload }).then(() => {
  //         toast.success(t('toasts.updated-cell'));
  //       });
  //     //update prices value by unit_price
  //     if (params.field == 'static_unit_price') {
  //       const updatedLines = purchaseRequest.lines.map((line) => {
  //         if (line.id === params.id) {
  //           line['total_price'] = Math.round(params.value * line['quantity']);
  //           line['static_unit_price'] = params.value;
  //         }
  //         return line;
  //       });
  //       setPurchaseRequest({ ...purchaseRequest, lines: updatedLines });
  //     }
  //     //update total price value by quantity
  //     if (params.field == 'quantity') {
  //       const updatedLines = purchaseRequest.lines.map((line) => {
  //         if (line.id === params.id) {
  //           line['total_price'] = params.value * line['static_unit_price'];
  //           line['quantity'] = params.value;
  //         }
  //         return line;
  //       });
  //       setPurchaseRequest({ ...purchaseRequest, lines: updatedLines });
  //     }
  //   } else {
  //     setPurchaseRequest({
  //       ...purchaseRequest,
  //       lines: purchaseRequest.lines.map((line) => {
  //         if (line.id === params.id) {
  //           if (params.value instanceof Date) {
  //             line[params.field] = dayjs(params.value).format('YYYY-MM-DD');
  //           } else {
  //             if (params.field == '_quantity') {
  //               line['quantity'] = params.value;
  //             } else {
  //               line[params.field] = params.value;
  //             }
  //           }
  //         }
  //         return line;
  //       })
  //     });
  //   }
  // };

  const processRowUpdate = async (
    newRow: GridRowModel,
    oldRow: GridRowModel
  ): Promise<GridRowModel> => {
    // Identifica quali campi sono cambiati confrontando new vs old
    const changedFields = Object.keys(newRow).filter((key) => newRow[key] !== oldRow[key]);

    // Assumiamo modifica singola (comportamento cella)
    const field = changedFields[0];
    const value = newRow[field];

    if (typeof newRow.id === 'number' && newRow.id > 0) {
      // Caso riga esistente - chiama API
      const payload: Record<string, any> = {};

      if (field === '_quantity') {
        payload['quantity'] = value;
      } else {
        payload[field] = value;
      }

      if (payload[field] !== undefined) {
        try {
          await purchaseRequestLineService.update(newRow.id, payload);
          toast.success(t('toasts.updated-cell'));
        } catch (e) {
          toast.error(t('toasts.update-failed'));
          throw e; // CRITICO: rilancia per far fallire la modifica in griglia
        }
      }

      // Calcola prezzi aggiornati
      const updatedRow = { ...newRow };

      if (field === 'static_unit_price') {
        updatedRow.total_price = Math.round(value * newRow.quantity);
      }

      if (field === '_quantity' || field === 'quantity') {
        updatedRow.total_price = value * newRow.static_unit_price;
        updatedRow.quantity = value;
      }

      return updatedRow;
    } else {
      // Caso riga nuova (id temporaneo) - aggiorna solo stato locale
      setPurchaseRequest((prev) => ({
        ...prev,
        lines: prev.lines.map((line) => {
          if (line.id === newRow.id) {
            const updates: any = {};

            if (value instanceof Date) {
              updates[field] = dayjs(value).format('YYYY-MM-DD');
            } else {
              if (field === '_quantity') {
                updates.quantity = value;
              } else {
                updates[field] = value;
              }
            }

            return { ...line, ...updates };
          }
          return line;
        })
      }));

      return newRow;
    }
  };

  // ------------------- SUBMIT - SINGLE && MULTI ROW -------------------

  /** Validate quantities, resolve contract references and submit a single or programmed row. */
  const onSubmitRow = (values) => {
    // ERRORE ! veniva perso il contract code siccome il "setContract" ora gestisce la contract_line
    // .....

    const matchingContract = values.contracts.find(
      (contract) => contract.id === values.contract_line
    );
    if (matchingContract) {
      values.contract = matchingContract.code;
    }
    // Per mantenere vecchia logica
    values.warehouse_obj = values.warehouse;
    values.warehouse = values.warehouse_obj.code;

    if (parseInt(values?.quantity || 0) === 0 && parseInt(values?.discounted_quantity || 0) === 0) {
      toast.error(t(`${toastTranlationPrefix}.quantity-error`));
      return;
    }

    // Access the TENANT field from the .env file
    const tenant = process.env.REACT_APP_TENANT;
    // MESTRE: per mestre, crema e molise il controllo per quantità multiple NON è RICHIESTO
    // Check if the quantity per units of package is valid
    if (
      tenant !== 'MAIN' &&
      unitsPerPackage &&
      (values.quantity || values.discounted_quantity) &&
      (parseInt(values?.quantity || 0) + parseInt(values?.discounted_quantity || 0)) %
        unitsPerPackage !==
        0
    ) {
      handleOpenConfirmationDialog(values);
    } else {
      onSubmitRowPostQuantityPerPacketCheck(values);
    }
  };

  const onSubmitRowPostQuantityPerPacketCheck = (values: any | null) => {
    // Check if the contract and warehouse are valid
    let errors = false;
    if (!contractOptions.map((c) => c.id).includes(values['contract_line'])) {
      toast.error(t(`${toastTranlationPrefix}.contract-error`));
      errors = true;
    }
    if (!warehouseOptions.map((c) => c.code).includes(values['warehouse'])) {
      toast.error(t(`${toastTranlationPrefix}.warehouse-error`));
      errors = true;
    }
    if (errors) return;
    // MULTIPLE ROWS
    if (values.programmed) {
      const startDate = dayjs(values.start_programmed);
      let currentDate = startDate,
        lastValidDay,
        lastValidDayOfWeek,
        lastValidWeek = 0,
        lastValidMonth;
      const endDate = dayjs(values.end_programmed);
      do {
        lastValidDay = currentDate.format('DD');
        lastValidDayOfWeek = currentDate.day();
        lastValidWeek += lastValidDayOfWeek === startDate.day() ? 1 : 0;
        lastValidMonth = currentDate.month();

        if (
          (values.rep_unit === 1 &&
            lastValidWeek % values.rep_frequency === 0 &&
            values.day_of_month == +lastValidDayOfWeek) ||
          (values.rep_unit === 2 &&
            (lastValidMonth - startDate.month()) % values.rep_frequency === 0 &&
            +values.day_of_month === +lastValidDay)
        ) {
          ((requested_shipment_date) => {
            _settingPurcheaseRequestSingleRow(values, requested_shipment_date);
          })(currentDate.format('YYYY-MM-DD'));
        }

        currentDate = currentDate.add(1, 'day');
      } while (currentDate.isBefore(endDate));
    } else {
      // SINGLE ROW
      _settingPurcheaseRequestSingleRow(values, null);
    }
  };

  /**
   * Internal helper that appends one or two lines (standard + discounted)
   * to the purchase request state.
   */
  function _settingPurcheaseRequestSingleRow(values: any, requested_shipment_date: any) {
    setPurchaseRequest((prevState) => {
      const newLines = [];
      // if the request is periodic
      if (values.programmed) {
        // check if the order is standard
        if (values.quantity > 0) {
          newLines.push({
            id: uuidv4(),
            ...values,
            requested_shipment_date,
            static_unit_price: unitPriceInfo,
            total_price: parseFloat(unitPriceInfo) * values.quantity,
            supplier: values.contracts[0].supplier,
            is_periodic: true,
            status: 1,
            index: prevState?.lines?.length + newLines.length + 1,
            note: values.notes || ''
          });
        }
        //Check if the order is discounted
        if (values.discounted_quantity > 0) {
          newLines.push({
            id: uuidv4(),
            ...values,
            discounted: true,
            requested_shipment_date,
            supplier: values.contracts[0].supplier,
            static_unit_price: unitPriceInfo,
            total_price: 0, // chiedere se è parseFloat(unitPriceInfo) * values.discounted_quantity,
            quantity: values.discounted_quantity,
            is_periodic: true,
            status: 1,
            index: prevState?.lines?.length + newLines.length + 1,
            note: values.notes || ''
          });
        }
      } else {
        // check if the order is standard
        if (values.quantity > 0) {
          newLines.push({
            id: uuidv4(),
            ...values,
            status: 1,
            supplier: values.contracts[0].supplier,
            static_unit_price: unitPriceInfo,
            total_price: parseFloat(unitPriceInfo) * values.quantity,
            index: prevState?.lines?.length + newLines.length + 1,
            note: values.notes || ''
          });
        }
        //Check if the order is discounted
        if (values.discounted_quantity > 0) {
          newLines.push({
            id: uuidv4(),
            ...values,
            discounted: true,
            static_unit_price: unitPriceInfo,
            supplier: values.contracts[0].supplier,
            total_price: 0, // chiedere se è parseFloat(unitPriceInfo) * values.discounted_quantity,
            quantity: values.discounted_quantity,
            status: 1,
            index: prevState?.lines?.length + newLines.length + 1,
            note: values.notes || ''
          });
        }
      }

      return {
        ...prevState,
        lines: prevState.lines.concat(newLines)
      };
    });
  }

  // ------------------- ORDER PREVIEW -------------------
  const getOrderPreview = () => {
    if (!allLinesSelected) {
      toast.error(t(`${toastTranlationPrefix}.lines-error`));
      return;
    }
    if (purchaseRequestId) {
      purchaseRequestService
        .getOrderPreview({ ...purchaseRequest, id: +purchaseRequestId })
        .then((res) => {
          setRequestPreview(res);
          setOpenPreviewDialog(true);
        });
    }
  };

  const supplierRecapText = (receptionFormat?: ISupplier['reception_format']) => {
    if (receptionFormat !== 'nessuno') {
      return t('pages.purchase-requests.order-preview-recap-bolla_elettronica');
    }
    return '';
  };

  const getOrderPreviewInnerContent = () => {
    return (
      requestPreview &&
      Object.keys(requestPreview)
        .filter((k) => k !== 'detail')
        .map((v, i) => {
          // Supplier
          const supplier = requestPreview[v];
          const supplierDetail = requestPreview[v]['detail'] as ISupplier;
          return (
            <div key={v + i} className="px-3 mb-3">
              <b>{supplierDetail['company_name']}</b>
              <div className="text-sm text-gray-600 mb-2">
                {supplierRecapText(supplierDetail.reception_format)}
              </div>
              <ul>
                {Object.keys(supplier)
                  .filter((k) => k !== 'detail')
                  .map((k) => {
                    const warehouse = requestPreview[v][k];
                    const warehouseDetail = requestPreview[v][k]['detail'] as IWarehouse;

                    const lines = warehouse['lines'] as IPurchaseRequestRow[];
                    return (
                      <div key={warehouseDetail.id} className="px-3">
                        {warehouseDetail.description}
                        <ul className="px-3">
                          {lines.map((line) => (
                            <li key={warehouse['id'] + '-' + line.id}>
                              <b>{line.product}</b> - {line.quantity}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
              </ul>
            </div>
          );
        })
    );
  };

  /* ======================== DIALOG HANDLERS ========================== */
  const handleCloseSendEmailDialog = () => {
    setOpenSendEmailDialog(false);
  };

  const handleClosePreviewDialog = () => {
    setOpenPreviewDialog(false);
  };

  const handleCloseConfirmationDialog = () => {
    setOpendConfirmationDialog(false);
  };

  const handleSubmitConfirmation = () => {
    onSubmitRowPostQuantityPerPacketCheck(confirmationValues);
    setConfirmationValues(null);
    setOpendConfirmationDialog(false);
  };

  const handleOpenConfirmationDialog = (values) => {
    setOpendConfirmationDialog(true);
    setConfirmationValues(values);
  };

  // utility function
  function mapLine(item) {
    return {
      ...item,
      id: +item.id || null,
      product: item.product['code']
    };
  }

  /* ======================== STATUS HANDLERS ========================== */
  // // Utility methods
  const handleDelete = (line) => {
    const index = purchaseRequest.lines.findIndex((l) => l.id === line.id);
    const nextLines = purchaseRequest.lines.map((obj, idx) => {
      if (index === idx) {
        return { ...obj, status: PurchaseRequestStatus.DELETED };
      }
      return obj;
    });
    const nextRequest = { ...purchaseRequest, lines: nextLines };
    setPurchaseRequest(nextRequest);
    persistRequestDraft(nextRequest).catch((e) => {
      toast.error(e.message);
    });
  };

  const approve = (line) => {
    const index = purchaseRequest.lines.findIndex((l) => l.id === line.id);

    changeRowStatus(index, PurchaseRequestStatus.APPROVED);
  };

  const preApprove = (line) => {
    const index = purchaseRequest.lines.findIndex((l) => l.id === line.id);

    changeRowStatus(index, PurchaseRequestStatus.PRE_APPROVED);
  };

  const reject = (line) => {
    const index = purchaseRequest.lines.findIndex((l) => l.id === line.id);

    changeRowStatus(index, PurchaseRequestStatus.REJECTED);
  };

  const changeRowStatus = (index, newStatus) => {
    const nextLines = purchaseRequest.lines.map((obj, idx) => {
      if (index === idx) {
        return {
          ...obj,
          status: newStatus
        };
      }
      return obj;
    });
    const nextRequest = { ...purchaseRequest, lines: nextLines };
    setPurchaseRequest(nextRequest);
    persistRequestDraft(nextRequest).catch((e) => {
      toast.error(e.message);
    });
  };

  // --------------------  Dialog  edit purchase req line --------------------
  const setOpenEditDialog = (row: any) => {
    setEditPurchaseRequestLine(row); // Set the current row data to be edited
    if (row.product) {
      contractLineService
        .getAllBaseInformation({ product_id: row.product.id, active: true, valid: true })
        .then(setEditContractOptions);
    }
    setOpenEditContractDialog(true); // Open the edit dialog
  };

  const handleCloseEditDialog = () => {
    setOpenEditContractDialog(false);
  };

  const onSubmitEdit = async (values) => {
    const lineIndex = purchaseRequest.lines.findIndex(
      (line) => line.id === editPurchaseRequestLine.id
    );
    const updatedLines = [...purchaseRequest.lines];

    updatedLines[lineIndex] = {
      ...updatedLines[lineIndex],
      quantity: values.quantity !== undefined ? values.quantity : updatedLines[lineIndex].quantity,
      static_unit_price:
        values.static_unit_price !== undefined
          ? values.static_unit_price
          : updatedLines[lineIndex].static_unit_price,
      requested_shipment_date:
        values.requested_shipment_date !== undefined
          ? values.requested_shipment_date
          : updatedLines[lineIndex].requested_shipment_date,
      notes: values.notes !== undefined ? values.notes : updatedLines[lineIndex].notes,
      contract: values.contract !== undefined ? values.contract : updatedLines[lineIndex].contract
    };
    setPurchaseRequest({
      ...purchaseRequest,
      lines: updatedLines
    });
    setOpenEditContractDialog(false);
  };

  const approveAll = () => {
    const updatedLines = purchaseRequest.lines.map((line) => ({
      ...line,
      status:
        line.status === PurchaseRequestStatus.DELETED
          ? PurchaseRequestStatus.DELETED
          : PurchaseRequestStatus.APPROVED
    }));
    setPurchaseRequest({ ...purchaseRequest, lines: updatedLines });
  };

  const rejectAll = () => {
    const updatedLines = purchaseRequest.lines.map((line) => ({
      ...line,
      status:
        line.status === PurchaseRequestStatus.DELETED
          ? PurchaseRequestStatus.DELETED
          : PurchaseRequestStatus.REJECTED
    }));
    setPurchaseRequest({ ...purchaseRequest, lines: updatedLines });
  };

  let columns: GridColDef[] = [];
  if (automaticGenerated) {
    columns = AutomaticPurchaseLinesColumns(
      reject,
      approve,
      preApprove,
      handleDelete,
      setOpenEditDialog,
      approveAll,
      rejectAll
    );
  } else {
    columns = PurchaseLinesColumns(
      reject,
      approve,
      preApprove,
      handleDelete,
      setOpenEditDialog,
      approveAll,
      rejectAll
    );
  }

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`, { id: purchaseRequestId })}
      subtitle={t(`${translationPrefix}.subtitle`)}
      entityCode={String(purchaseRequestId)}
      menuRight={
        <div className="flex items-center justify-end">
          <div className="w-60">
            <CustomizedSteppers status={purchaseRequest.status} />
          </div>
          {purchaseRequest.status !== 'ORDER_SENT' && (
            <ButtonGroup size="small" variant="outlined">
              <Button disabled={changesToSave()} onClick={saveRequest}>
                <Tooltip title={t('actions.save')}>
                  <SaveIcon />
                </Tooltip>
              </Button>
              <IsTenantCoordinator>
                <Button disabled={!changesToSave()} onClick={reviewRequest}>
                  {t(`${translationPrefix}.pre-approve`)}
                </Button>
              </IsTenantCoordinator>
              {purchaseRequest.id && (
                <IsTenantAdministrator>
                  <Button
                    disabled={!changesToSave()}
                    color={allLinesSelected ? 'success' : 'warning'}
                    onClick={getOrderPreview}>
                    {t(`pages.purchase-requests.generate-order`)}
                  </Button>
                </IsTenantAdministrator>
              )}
            </ButtonGroup>
          )}
        </div>
      }>
      {/* Form per la richiesta di acquisto */}
      <PurchaseRequestAccordion
        productOptions={productOptions}
        contractOptions={contractOptions}
        warehouseOptions={warehouseOptions}
        maxUnits={maxUnits}
        maxDiscountedUnits={maxDiscountedUnits}
        visibleProduct={visibleProduct}
        onSubmitRow={onSubmitRow}
        setProductToAdd={setProductToAdd}
        setContractToAdd={setContractToAdd}
        setWarehouseToAdd={setWarehouseToAdd}
        automaticGenerated={automaticGenerated}
      />
      {/* Item list */}
      <EnhancedCard
        title={'Lista Articoli'}
        subtitle="Controlla i codici approvati e le azioni disponibili prima dell'invio definitivo.">
        <DataGridPremium
          density="compact"
          initialState={{
            pinnedColumns: {
              left: ['code', 'approved'],
              right: ['actions']
            }
          }}
          rows={purchaseRequest.lines
            .filter((row) => row.status !== PurchaseRequestStatus.DELETED)
            .map((l) => ({
              id: uuidv4(),
              ...l,
              description: l.product['description']
            }))}
          localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
          slots={{
            toolbar: CustomToolbar
          }}
          sx={{ border: 'none' }}
          disableRowSelectionOnClick
          paginationModel={{ page: 0, pageSize: 10 }}
          onPaginationModelChange={(model) => console.log(model)}
          pageSizeOptions={[5, 10, 20, 50, 100, 500]}
          columns={columns}
          processRowUpdate={processRowUpdate}
          editMode="row"
        />
      </EnhancedCard>
      {/* Edit  dialog */}
      <EditPurchaseRequestLineDialog
        open={openEditContractDialog}
        onClose={handleCloseEditDialog}
        editPurchaseRequestLine={editPurchaseRequestLine}
        automaticGenerated={automaticGenerated}
        onSubmitEdit={onSubmitEdit}
        editContractOptions={editContractOptions}
      />
      {/* Show mail preview dialog */}
      <SendPurchaseOrderEmailDialog
        open={openSendEmailDialog}
        onClose={handleCloseSendEmailDialog}
        order_id={purchaseRequest.id}
        onSubmit={sendOrder}
        lines={purchaseRequest.lines}
      />
      {/* Send order */}
      <PurchaseOrderPreviewDialog
        open={openPreviewDialog}
        onClose={handleClosePreviewDialog}
        fulfillRequest={fulfillRequest}
        getOrderPreviewInnerContent={getOrderPreviewInnerContent}
        canSendEmail={true}
      />
      {/* Confirmation dialog */}
      <QuantityValidationConfirmationDialog
        open={opendConfirmationDialog}
        onClose={handleCloseConfirmationDialog}
        onConfirm={handleSubmitConfirmation}
      />
    </TenantPage>
  );
};

export default PurchaseRequestPage;
